// CRM Opportunity record — HubSpot-style detail page. Header with won/lost actions,
// clickable stage progression stepper, three columns (about / activity+notes /
// associations) and a collapsible docked analyst chat fed with this opportunity's
// context. Editing happens here; the board modal is create-only.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import raptorIcon from '../../assets/raptor.svg';

const CUR = { GBP: '\u00A3', USD: '$', EUR: '\u20AC', NGN: '\u20A6', CAD: 'CA$', AUD: 'A$' };
const CURRENCIES = Object.keys(CUR);
const PRIORITIES = ['Low', 'Medium', 'High'];
const OPP_TYPES = ['New business', 'Existing business', 'Renewal', 'Upsell'];

const money = (v, c) => (CUR[c] || (c ? c + ' ' : '\u00A3')) + Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });

function fmtDate(v) {
  if (!v) return '\u2014';
  const d = new Date(v);
  if (isNaN(d)) return '\u2014';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtDateTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ', ' +
         d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function relTime(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  if (s < 604800) return Math.floor(s / 86400) + 'd ago';
  return fmtDate(iso);
}

const HIST_STYLE = {
  opportunity_created: { icon: '\uFF0B', c: '#3F4956', bg: '#E3E7ED' },
  opportunity_stage_moved: { icon: '\u2192', c: '#2E8FFF', bg: '#E3EEFF' },
  opportunity_won: { icon: '\u2713', c: '#00A878', bg: '#DFF5EE' },
  opportunity_lost: { icon: '\u00D7', c: '#F43F5E', bg: '#FDE7EB' }
};

function LostReasonModal({ onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  return (
    <div className="crmor-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="crmor-modal">
        <div className="crmor-modal-h"><h3>Mark as lost</h3><button className="crmor-x" onClick={onCancel}>{'\u00D7'}</button></div>
        <div className="crmor-modal-b">
          <label className="crmor-lbl">Lost reason</label>
          <textarea className="crmor-inp crmor-ta" rows="3" autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Went with a competitor, budget cut, timing" />
        </div>
        <div className="crmor-modal-f">
          <button className="crmor-btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="crmor-btn-rose" onClick={() => onConfirm(reason.trim())}>Mark lost</button>
        </div>
      </div>
    </div>
  );
}

// Collapsible docked analyst chat, fed with this opportunity's context
function AnalystDock({ clientId, contextStr, collapsed, setCollapsed }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    const next = [...msgs, { role: 'user', content: text }];
    setMsgs(next);
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/intelligence/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history: msgs.slice(-12), context: contextStr })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Chat failed');
      setMsgs(m => [...m, { role: 'assistant', content: d.answer || '' }]);
    } catch (e) {
      toast.error(e.message);
      setMsgs(m => [...m, { role: 'assistant', content: 'Something went wrong. Try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  if (collapsed) {
    return (
      <button className="crmor-dock-open" onClick={() => setCollapsed(false)} title="Open Raptor">
        <span className="crmor-dock-open-ico">{'\u2726'}</span>
        <span className="crmor-dock-open-lbl">Raptor</span>
      </button>
    );
  }

  return (
    <aside className="crmor-dock">
      <div className="crmor-dock-h">
        <span className="crmor-dock-ico">{'\u2726'}</span>
        <span className="crmor-dock-title">Raptor</span>
        <button className="crmor-dock-collapse" onClick={() => setCollapsed(true)} title="Collapse">{'\u00BB'}</button>
      </div>
      <div className="crmor-dock-body" ref={bodyRef}>
        {msgs.length === 0 && (
          <div className="crmor-dock-intro">
            Ask about this opportunity. Raptor knows its stage, value, history and the linked company's signals.
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={'crmor-dock-msg' + (m.role === 'user' ? ' crmor-dock-msg--me' : '')}>{m.content}</div>
        ))}
        {busy && <div className="crmor-dock-msg crmor-dock-typing">Thinking{'\u2026'}</div>}
      </div>
      <div className="crmor-dock-foot crmor-dock-foot--icon">
        <img className="crm-composer-raptor" src={raptorIcon} alt="Raptor" />
        <textarea className="crmor-dock-input" rows="2" value={input}
          placeholder="Ask Raptor..."
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <button className="crmor-btn crmor-dock-send" onClick={send} disabled={busy || !input.trim()}>Send</button>
      </div>
    </aside>
  );
}

export default function CrmOpportunityRecord() {
  const { oppId } = useParams();
  const { clientId } = useOutletContext();
  const navigate = useNavigate();
  const { user } = useUser();
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';

  const [opp, setOpp] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Activity');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [lostModal, setLostModal] = useState(false);
  const [dockCollapsed, setDockCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/opportunities/${oppId}`);
      if (!r.ok) throw new Error('Opportunity not found');
      const d = await r.json();
      setOpp(d);
      const pr = await fetch(`${API_BASE}/clients/${clientId}/pipelines`);
      const pd = await pr.json();
      setPipeline(pd.find(p => p.id === d.pipeline_id) || null);
      try {
        const nR = await fetch(`${API_BASE}/opportunities/${oppId}/notes`);
        const nd = await nR.json();
        setNotes(Array.isArray(nd) ? nd : []);
      } catch (e) {}
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [oppId, clientId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (body, okMsg) => {
    try {
      const r = await fetch(`${API_BASE}/opportunities/${oppId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Update failed');
      if (okMsg) toast.success(okMsg);
      load();
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    }
  };

  const moveToStage = async (stage) => {
    if (!opp || stage.id === opp.stage_id) return;
    if (stage.stage_type === 'lost') { setLostModal(true); return; }
    patch({ stage_id: stage.id }, `Moved to ${stage.name}`);
  };

  const markWon = () => {
    const won = pipeline && pipeline.stages.find(s => s.stage_type === 'won');
    if (!won) { toast.error('This pipeline has no won stage. Add one in Edit pipeline.'); return; }
    patch({ stage_id: won.id }, 'Marked won');
  };
  const markLost = () => {
    const lost = pipeline && pipeline.stages.find(s => s.stage_type === 'lost');
    if (!lost) { toast.error('This pipeline has no lost stage. Add one in Edit pipeline.'); return; }
    setLostModal(true);
  };
  const confirmLost = async (reason) => {
    setLostModal(false);
    const lost = pipeline.stages.find(s => s.stage_type === 'lost');
    patch({ stage_id: lost.id, lost_reason: reason || null }, 'Marked lost');
  };
  const reopen = () => {
    const open = pipeline && pipeline.stages.find(s => s.stage_type === 'open');
    if (!open) { toast.error('This pipeline has no open stage.'); return; }
    patch({ stage_id: open.id }, 'Reopened');
  };

  const startEdit = () => {
    setForm({
      name: opp.name,
      value: String(parseFloat(opp.value)),
      currency: opp.currency || 'GBP',
      open_date: opp.open_date ? opp.open_date.slice(0, 10) : '',
      expected_close_date: opp.expected_close_date ? opp.expected_close_date.slice(0, 10) : '',
      priority: opp.priority || '',
      opp_type: opp.opp_type || '',
      owner_name: opp.owner_name || ''
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    const ok = await patch({
      name: form.name.trim(),
      value: form.value === '' ? 0 : parseFloat(form.value),
      currency: form.currency,
      open_date: form.open_date || null,
      expected_close_date: form.expected_close_date || null,
      priority: form.priority || null,
      opp_type: form.opp_type || null,
      owner_name: form.owner_name.trim() || null
    }, 'Saved');
    if (ok) setEditing(false);
  };

  const del = async () => {
    if (!window.confirm('Delete this opportunity? This cannot be undone.')) return;
    try {
      const r = await fetch(`${API_BASE}/opportunities/${oppId}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Opportunity deleted');
      navigate(`/clients/${clientId}/crm/opportunities`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const r = await fetch(`${API_BASE}/opportunities/${oppId}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, author_email: userEmail, author_name: userEmail.split('@')[0] || 'You', content: newNote.trim() })
      });
      const note = await r.json();
      setNotes(prev => [...prev, note]);
      setNewNote('');
    } catch (e) {
      toast.error('Failed to add note');
    }
  };
  const deleteNote = async (noteId) => {
    try {
      await fetch(`${API_BASE}/opportunities/${oppId}/notes/${noteId}`, { method: 'DELETE' });
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {}
  };

  if (loading) return <div className="crmor-load">Loading opportunity{'\u2026'}</div>;
  if (!opp) return <div className="crmor-load">Opportunity not found.</div>;

  const stages = pipeline ? pipeline.stages : [];
  const curIdx = stages.findIndex(s => s.id === opp.stage_id);
  const isClosed = opp.status !== 'open';

  const contextStr = [
    `Opportunity: ${opp.name}`,
    `Pipeline: ${opp.pipeline_name} | Stage: ${opp.stage_name} (${opp.win_probability}% probability) | Status: ${opp.status}`,
    `Amount: ${money(opp.value, opp.currency)} | Open: ${fmtDate(opp.open_date)} | Expected close: ${fmtDate(opp.expected_close_date)}`,
    opp.priority ? `Priority: ${opp.priority}` : '',
    opp.opp_type ? `Type: ${opp.opp_type}` : '',
    opp.company_name ? `Company: ${opp.company_name} (id ${opp.company_id})${opp.company_website ? ' ' + opp.company_website : ''}` : '',
    opp.contact_name ? `Contact: ${opp.contact_name}${opp.contact_email ? ' ' + opp.contact_email : ''}` : '',
    opp.lost_reason ? `Lost reason: ${opp.lost_reason}` : '',
    opp.notes ? `Notes: ${opp.notes}` : '',
    (opp.history || []).length ? 'Recent history:\n' + opp.history.slice(0, 10).map(h => `- ${fmtDate(h.created_at)}: ${h.detail}`).join('\n') : ''
  ].filter(Boolean).join('\n');

  return (
    <div className="crmor-page">
      <div className="crmor-main">
        <div className="crmor-crumb">
          <Link to={`/clients/${clientId}/crm/opportunities`} className="crmor-back">{'\u2190'} Opportunities</Link>
          <span className="crmor-crumb-name">{opp.name}</span>
          <span className="crmor-crumb-pipe">{opp.pipeline_name}</span>
        </div>

        <div className="crmor-head">
          <div className="crmor-head-l">
            <h1>{opp.name}</h1>
            <div className="crmor-head-meta">
              <span className="crmor-amt">{money(opp.value, opp.currency)}</span>
              <span className="crmor-stagepill" style={{ background: (opp.stage_colour || '#64748B') + '1A', color: opp.stage_colour }}>{opp.stage_name}</span>
              {opp.status === 'won' && <span className="crmor-statuspill crmor-statuspill--won">Won {fmtDate(opp.won_at)}</span>}
              {opp.status === 'lost' && <span className="crmor-statuspill crmor-statuspill--lost">Lost {fmtDate(opp.lost_at)}</span>}
            </div>
          </div>
          <div className="crmor-head-r">
            {!isClosed && <button className="crmor-btn-green" onClick={markWon}>Mark won</button>}
            {!isClosed && <button className="crmor-btn-rose" onClick={markLost}>Mark lost</button>}
            {isClosed && <button className="crmor-btn-ghost" onClick={reopen}>Reopen</button>}
            <button className="crmor-btn-ghost crmor-btn-ghost--danger" onClick={del}>Delete</button>
          </div>
        </div>

        {stages.length > 0 && (
          <div className="crmor-stepper">
            {stages.map((s, i) => {
              const reached = curIdx >= 0 && i <= curIdx;
              const isCur = s.id === opp.stage_id;
              return (
                <button key={s.id}
                        className={'crmor-step' + (reached ? ' crmor-step--on' : '') + (isCur ? ' crmor-step--cur' : '')}
                        style={reached ? { background: s.colour, borderColor: s.colour } : {}}
                        onClick={() => moveToStage(s)}
                        title={s.stage_type === 'open' ? `${s.name} \u00B7 ${s.win_probability}%` : s.name}>
                  {s.name}
                </button>
              );
            })}
          </div>
        )}

        <div className="crmor-cols">
          <div className="crmor-col-l">
            <div className="crmor-card">
              <div className="crmor-cardh">About this opportunity
                {!editing
                  ? <button className="crmor-cardh-btn" onClick={startEdit}>Edit</button>
                  : <span className="crmor-cardh-btns">
                      <button className="crmor-cardh-btn" onClick={() => setEditing(false)}>Cancel</button>
                      <button className="crmor-cardh-btn crmor-cardh-btn--save" onClick={saveEdit}>Save</button>
                    </span>}
              </div>
              {!editing ? (
                <div className="crmor-fields">
                  <div className="crmor-field"><span>Amount</span><b>{money(opp.value, opp.currency)}</b></div>
                  <div className="crmor-field"><span>Currency</span><b>{opp.currency || 'GBP'}</b></div>
                  <div className="crmor-field"><span>Pipeline</span><b>{opp.pipeline_name}</b></div>
                  <div className="crmor-field"><span>Open date</span><b>{fmtDate(opp.open_date)}</b></div>
                  <div className="crmor-field"><span>Expected close</span><b>{fmtDate(opp.expected_close_date)}</b></div>
                  <div className="crmor-field"><span>Priority</span><b>{opp.priority || '\u2014'}</b></div>
                  <div className="crmor-field"><span>Type</span><b>{opp.opp_type || '\u2014'}</b></div>
                  <div className="crmor-field"><span>Owner</span><b>{opp.owner_name || '\u2014'}</b></div>
                  {opp.lost_reason && <div className="crmor-field"><span>Lost reason</span><b>{opp.lost_reason}</b></div>}
                  <div className="crmor-field"><span>In stage since</span><b>{fmtDate(opp.stage_entered_at)}</b></div>
                </div>
              ) : (
                <div className="crmor-fields crmor-fields--edit">
                  <label className="crmor-lbl">Name</label>
                  <input className="crmor-inp" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  <div className="crmor-grid2">
                    <div>
                      <label className="crmor-lbl">Amount</label>
                      <input className="crmor-inp" type="number" min="0" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
                    </div>
                    <div>
                      <label className="crmor-lbl">Currency</label>
                      <select className="crmor-inp" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                        {CURRENCIES.map(c => <option key={c} value={c}>{c} ({CUR[c]})</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="crmor-grid2">
                    <div>
                      <label className="crmor-lbl">Open date</label>
                      <input className="crmor-inp" type="date" value={form.open_date} onChange={e => setForm(f => ({ ...f, open_date: e.target.value }))} />
                    </div>
                    <div>
                      <label className="crmor-lbl">Expected close</label>
                      <input className="crmor-inp" type="date" value={form.expected_close_date} onChange={e => setForm(f => ({ ...f, expected_close_date: e.target.value }))} />
                    </div>
                  </div>
                  <div className="crmor-grid2">
                    <div>
                      <label className="crmor-lbl">Priority</label>
                      <select className="crmor-inp" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                        <option value="">None</option>
                        {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="crmor-lbl">Type</label>
                      <select className="crmor-inp" value={form.opp_type} onChange={e => setForm(f => ({ ...f, opp_type: e.target.value }))}>
                        <option value="">None</option>
                        {OPP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <label className="crmor-lbl">Owner</label>
                  <input className="crmor-inp" value={form.owner_name} onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))} placeholder="Who owns this opportunity" />
                </div>
              )}
            </div>
          </div>

          <div className="crmor-col-m">
            <div className="crmor-card crmor-card--tabs">
              <div className="crmor-tabs">
                {['Activity', 'Notes'].map(t => (
                  <button key={t} className={'crmor-tab' + (tab === t ? ' crmor-tab--on' : '')} onClick={() => setTab(t)}>
                    {t}{t === 'Notes' && notes.length > 0 ? ` (${notes.length})` : ''}
                  </button>
                ))}
              </div>

              {tab === 'Activity' && (
                <div className="crmor-timeline">
                  {(opp.history || []).length === 0 && <div className="crmor-empty-sm">No activity yet.</div>}
                  {(opp.history || []).map((h, i) => {
                    const st = HIST_STYLE[h.action_type] || { icon: '\u00B7', c: '#64748B', bg: '#EEF0F3' };
                    return (
                      <div key={i} className="crmor-tl-row">
                        <span className="crmor-tl-ico" style={{ color: st.c, background: st.bg }}>{st.icon}</span>
                        <div className="crmor-tl-body">
                          <div className="crmor-tl-text">{h.detail}</div>
                          <div className="crmor-tl-time">{fmtDateTime(h.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === 'Notes' && (
                <div className="crmor-notes">
                  <div className="crmor-notes-list">
                    {notes.length === 0 && <div className="crmor-empty-sm">No notes yet. Add the first one below.</div>}
                    {notes.map(note => {
                      const isMe = note.author_email === userEmail;
                      return (
                        <div key={note.id} className={'crmor-note' + (isMe ? ' crmor-note--me' : '')}>
                          <div className="crmor-note-meta">
                            <span>{note.author_name || 'Someone'}</span>
                            <span>{relTime(note.created_at)}</span>
                            {isMe && <button className="crmor-note-del" onClick={() => deleteNote(note.id)} title="Delete">{'\u00D7'}</button>}
                          </div>
                          <div className="crmor-note-body">{note.content}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="crmor-note-compose">
                    <textarea className="crmor-inp crmor-ta" rows="2" value={newNote}
                      placeholder="Add a note... (Enter to send, Shift+Enter for new line)"
                      onChange={e => setNewNote(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }} />
                    <button className="crmor-btn" onClick={addNote} disabled={!newNote.trim()}>Send</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="crmor-col-r">
            <div className="crmor-card">
              <div className="crmor-cardh">Company</div>
              {opp.company_id ? (
                <div className="crmor-assoc">
                  <Link className="crmor-assoc-name" to={`/clients/${clientId}/crm/companies/${opp.company_id}`}>{opp.company_name}</Link>
                  {opp.company_website && <div className="crmor-assoc-sub">{opp.company_website.replace(/^https?:\/\//, '')}</div>}
                </div>
              ) : <div className="crmor-empty-sm">No company linked.</div>}
            </div>
            <div className="crmor-card">
              <div className="crmor-cardh">Contact</div>
              {opp.contact_id ? (
                <div className="crmor-assoc">
                  <Link className="crmor-assoc-name" to={`/clients/${clientId}/crm/contacts/${opp.contact_id}`}>{opp.contact_name}</Link>
                  {opp.contact_email && <div className="crmor-assoc-sub">{opp.contact_email}</div>}
                </div>
              ) : <div className="crmor-empty-sm">No contact linked.</div>}
            </div>
          </div>
        </div>
      </div>

      <AnalystDock clientId={clientId} contextStr={contextStr} collapsed={dockCollapsed} setCollapsed={setDockCollapsed} />

      {lostModal && <LostReasonModal onCancel={() => setLostModal(false)} onConfirm={confirmLost} />}
    </div>
  );
}
